import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Smartphone, FileChartColumn, HeadphonesIcon, FolderKanban, ArrowRight, ExternalLink } from "lucide-react";
import StatusBadge from "@/components/common/StatusBadge";
import { format } from "date-fns";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { SectionHelp } from "@/components/help";
import { useReportTileEmpty } from "./DashboardTile";

interface Project {
  id: number;
  name: string;
  objective: string;
  startDate: string;
  endDate: string;
  status: "draft" | "active" | "review" | "completed";
  department: string;
  progress: number;
}

const projectColors = [
  { bg: "bg-gradient-to-br from-blue-500 to-indigo-600", light: "bg-blue-50 dark:bg-blue-950/30" },
  { bg: "bg-gradient-to-br from-emerald-500 to-teal-600", light: "bg-emerald-50 dark:bg-emerald-950/30" },
  { bg: "bg-gradient-to-br from-amber-500 to-orange-600", light: "bg-amber-50 dark:bg-amber-950/30" },
  { bg: "bg-gradient-to-br from-rose-500 to-pink-600", light: "bg-rose-50 dark:bg-rose-950/30" },
];

type ProjectTaskCounts = Record<number, { totalTasks: number; completedTasks: number }>;

export default function ProjectsTable() {
  const { data: projects, isLoading } = useQuery<Project[]>({
    queryKey: ["/api/projects"]
  });
  const { data: taskCounts } = useQuery<ProjectTaskCounts>({
    queryKey: ["/api/projects/task-counts"],
  });
  const [, navigate] = useLocation();

  useReportTileEmpty("projects_table", !isLoading && (projects ?? []).length === 0);

  const getProjectIcon = (name: string) => {
    if (name.toLowerCase().includes("mobile") || name.toLowerCase().includes("app")) {
      return <Smartphone className="h-5 w-5" />;
    } else if (name.toLowerCase().includes("okr") || name.toLowerCase().includes("planning")) {
      return <FileChartColumn className="h-5 w-5" />;
    } else {
      return <HeadphonesIcon className="h-5 w-5" />;
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 75) return "bg-emerald-500";
    if (progress >= 50) return "bg-blue-500";
    if (progress >= 25) return "bg-amber-500";
    return "bg-muted-foreground";
  };

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-4 border-b border-border flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
            <FolderKanban className="h-5 w-5 text-white" />
          </div>
          <div>
            <SectionHelp contentId="dashboard:projects" tooltip="Learn about your projects">
              <CardTitle className="text-lg font-bold">My Projects</CardTitle>
            </SectionHelp>
            <p className="text-sm text-muted-foreground">Active projects you're working on</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 gap-1 font-semibold"
          onClick={() => navigate("/projects")}
          aria-label="View all projects"
        >
          View All
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardHeader>
      
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-6 space-y-4">
            <ProjectRowSkeleton />
            <ProjectRowSkeleton />
            <ProjectRowSkeleton />
          </div>
        ) : projects && projects.length > 0 ? (
          <div className="divide-y divide-border">
            {projects.slice(0, 4).map((project, index) => {
              const colorScheme = projectColors[index % projectColors.length];
              return (
                <div 
                  key={project.id} 
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className={cn(
                    "group flex items-center gap-4 p-5 cursor-pointer",
                    "hover:bg-muted/80 transition-all duration-200"
                  )}
                >
                  <div className={cn(
                    "flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-md",
                    colorScheme.bg,
                    "group-hover:scale-105 transition-transform duration-200"
                  )}>
                    {getProjectIcon(project.name)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground group-hover:text-indigo-600 transition-colors truncate">
                        {project.name}
                      </h3>
                      <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{project.department}</p>
                  </div>

                  <div className="hidden sm:block flex-shrink-0">
                    <StatusBadge status={project.status} />
                  </div>

                  <div className="hidden md:block flex-shrink-0 text-right">
                    <p className="text-sm font-medium text-foreground">
                      {project.endDate ? format(new Date(project.endDate), "MMM d, yyyy") : "No deadline"}
                    </p>
                    <p className="text-xs text-muted-foreground">Due date</p>
                  </div>

                  <div className="w-28 flex-shrink-0">
                    {(() => {
                      const counts = taskCounts?.[project.id];
                      const total = counts?.totalTasks ?? 0;
                      const completed = counts?.completedTasks ?? 0;
                      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
                      return total > 0 ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className={cn("h-full rounded-full transition-all duration-500", getProgressColor(percentage))}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-foreground w-16 text-right">
                            {completed}/{total}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Not Started</span>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FolderKanban className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium">No projects available</p>
            <p className="text-sm text-muted-foreground mt-1">Create your first project to get started</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProjectRowSkeleton() {
  return (
    <div className="flex items-center gap-4 p-5">
      <Skeleton className="w-12 h-12 rounded-xl" />
      <div className="flex-1">
        <Skeleton className="h-5 w-40 mb-2" />
        <Skeleton className="h-4 w-24" />
      </div>
      <Skeleton className="h-6 w-20 rounded-full hidden sm:block" />
      <Skeleton className="h-8 w-24 hidden md:block" />
      <div className="w-28 flex items-center gap-2">
        <Skeleton className="h-2 flex-1 rounded-full" />
        <Skeleton className="h-4 w-10" />
      </div>
    </div>
  );
}
